"use client";

import { Button } from "@/components/ui/button";
import { 
  UserButton, 
  SignInButton, 
  SignedIn, 
  SignedOut, 
  useUser 
} from "@clerk/nextjs";
import { PenSquare } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

export function AuthNavbar() {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isLoaded } = useUser();
  const queryClient = useQueryClient();

  const handleNewChat = useCallback(() => {
    // If already on home, refresh to get a new chat ID
    if (pathname === "/") {
      router.refresh();
    } else {
      router.push("/");
    }
  }, [pathname, router]);

  // Handle cleanup when user logs out
  useEffect(() => {
    // Check if user just logged out (was logged in, now is not)
    if (isLoaded && !user) {
      const wasLoggedIn = sessionStorage.getItem('wasLoggedIn');
      
      if (wasLoggedIn === 'true') {
        // Clear the flag
        sessionStorage.removeItem('wasLoggedIn');
        
        // Clear sensitive data from localStorage (excluding MCP servers and UI preferences)
        const keysToPreserve = ['theme', 'sidebar-open', 'selectedModel', 'mcp-servers', 'selected-mcp-servers', 'age-verified'];
        const preservedData: Record<string, string> = {};
        
        // Preserve non-sensitive UI preferences
        keysToPreserve.forEach(key => {
          const value = localStorage.getItem(key);
          if (value) preservedData[key] = value;
        });
        
        // Clear all localStorage
        localStorage.clear();
        
        // Restore UI preferences
        Object.entries(preservedData).forEach(([key, value]) => {
          localStorage.setItem(key, value);
        });
        
        // Clear all React Query cache
        queryClient.clear();
        
        // Redirect to home
        window.location.href = "/";
      }
    } else if (isLoaded && user) {
      // Set flag when user is logged in
      sessionStorage.setItem('wasLoggedIn', 'true');
    }
  }, [user, isLoaded, queryClient]);

  // Keyboard shortcut: Cmd/Ctrl + K for new chat
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        handleNewChat();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNewChat]);

  return (
    <div className="flex items-center gap-2">
      <Button
        onClick={handleNewChat}
        size="sm"
        variant="ghost"
        className="gap-2 hidden sm:flex transition-all border border-border cursor-pointer"
        title="New Chat (⌘K / Ctrl+K)"
      >
        <PenSquare className="h-4 w-4" />
        <span>New Chat</span>
      </Button>
      <Button
        onClick={handleNewChat}
        size="sm"
        variant="ghost"
        className="gap-2 sm:hidden p-2 transition-all border border-border cursor-pointer"
        title="New Chat"
      >
        <PenSquare className="h-4 w-4" />
      </Button>
      <SignedIn>
        <UserButton 
          afterSignOutUrl="/"
          appearance={{
            elements: {
              avatarBox: "w-8 h-8",
            },
          }}
        />
      </SignedIn>
      <SignedOut>
        <SignInButton mode="modal">
          <Button size="sm" className="gap-2">
            Sign In
          </Button>
        </SignInButton>
      </SignedOut>
    </div>
  );
}
